import { FormEvent, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { Autocomplete, Button, FormControl, TextField } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectFilter } from "../../../store/qas/qasSlice";
import {
	addQuestion,
	approveCandidateList,
	clearQuestionsList,
	getQuestionsList,
	selectAddingQuestion,
	setupDeletionOnlyCandidates,
} from "../../../store/qas/qasQuestionSlice";
import { selectCategoriesList } from "@/store/qas/qasCategorySlice";
import useAccessRight from "../../../hooks/useAccessRight";
import useTranslate from "../../../hooks/useTranslate";
import { QAS } from "../../../constants/accessRights";
import { QAS_QUESTION_ID } from "../../../constants/cookieNames";
import { colorPrimary } from "../../../constants/colors";
import { ICategory } from "../../../types/qas/qasCategoryTypes";
import { RequestStatus, ResponseStatus } from "../../../types/statusTypes";
import ModalFormWindow from "../../../HOC/ModalFormWindow/ModalFormWindow";
import NoOptionText from "../../../ui/NoOptionText/NoOptionText";
import ProgressCircle from "../../MUI/ProgressCircle/ProgressCircle";
import { IFormAddingQuestionProps } from "./FormAddingQuestion.props";

const FormAddingQuestion = ({ showModal, setShowModal, setShowNotification, candidate, candidateId }: IFormAddingQuestionProps): JSX.Element => {
	const [questionInput, setQuestionInput] = useState<string>(candidate || ""); // вопрос

	const dispatch = useAppDispatch();
	const filter = useAppSelector(selectFilter); // store - фильтр
	const categoriesList = useAppSelector(selectCategoriesList); // store - список категорий
	const addingQuestionStatus = useAppSelector(selectAddingQuestion); // store - статус добавления вопроса

	const [inputCategory, setInputCategory] = useState<ICategory | null>(filter.category); // категория

	const [_cookies, setCookie] = useCookies([QAS_QUESTION_ID]); // hook для работы с cookie
	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// следим за статусом добавления вопроса
	useEffect(() => {
		// если вопрос добавился и без ошибок
		if (addingQuestionStatus.error === ResponseStatus.SUCCESS && addingQuestionStatus.status === RequestStatus.IDLE && addingQuestionStatus.message !== "") {
			setCookie(QAS_QUESTION_ID, addingQuestionStatus.id, { path: "/", maxAge: 2_592_000 }); // устанавливаем cookie на месяц
			// если создавали с нуля - (не из кандидата)
			if (!(candidate && candidateId)) {
				dispatch(clearQuestionsList()); // очищаем список вопросов
				isAccess(QAS.QUESTION_LIST) && dispatch(getQuestionsList({ answer: filter.answer, question: filter.question, category: filter.category?.id })); // получаем заново список вопросов
			} else {
				dispatch(setupDeletionOnlyCandidates()); // установка флага только удаления кандидатов
				dispatch(
					approveCandidateList({
						destinationQuestionId: "-1",
						candidateList: [{ id: candidateId, candidate }],
					})
				); // удаление кандидата
			}
		}

		// если изменился статус добавления
		if (addingQuestionStatus.error !== ResponseStatus.SUCCESS || addingQuestionStatus.status === RequestStatus.FAILED || addingQuestionStatus.message !== "") {
			handleClose(); // закрываем форму
			setShowNotification(true); // включаем уведомление
		}
	}, [addingQuestionStatus]);

	// обработчик добавления вопроса
	const submitHandler = (e: FormEvent<HTMLFormElement>): void => {
		e.preventDefault();
		inputCategory && dispatch(addQuestion({ question: questionInput, categoryId: inputCategory?.id })); // добавление вопроса
	};

	// обработчик закрытия формы
	const handleClose = (): void => {
		// если идет добавление вопроса - запрещаем покидать форму
		if (addingQuestionStatus.status === RequestStatus.LOADING) return;
		setShowModal(false); // закрытие модального окна
	};

	return (
		<ModalFormWindow showModal={showModal} setShowModal={setShowModal} headerTitle="title_addingQuestion" close={handleClose}>
			<form onSubmit={submitHandler}>
				<FormControl fullWidth margin="dense">
					<Autocomplete
						autoHighlight
						options={categoriesList.data}
						value={inputCategory}
						onChange={(_, value) => setInputCategory(value)}
						noOptionsText={<NoOptionText />}
						disabled={addingQuestionStatus.status === RequestStatus.LOADING}
						renderInput={(params) => (
							<TextField
								{...params}
								required
								label={translate("select_category")}
								InputLabelProps={{
									style: {
										fontSize: 13,
									},
								}}
								sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 } }}
							/>
						)}
						sx={{
							".MuiInputBase-root": { height: 33, fontSize: 13, color: colorPrimary },
							".MuiInputBase-input": { marginTop: -1 },
						}}
						getOptionLabel={(option) => option.name}
						renderOption={(props, option) => {
							return (
								<span {...props} style={{ fontSize: 13, color: colorPrimary, textAlign: "left" }} key={option.id}>
									{option.name}
								</span>
							);
						}}
					/>
				</FormControl>
				<FormControl fullWidth margin="dense">
					<TextField
						autoFocus
						required
						multiline
						minRows={2}
						maxRows={7}
						disabled={addingQuestionStatus.status === RequestStatus.LOADING}
						id="question"
						label={translate("input_question")}
						variant="outlined"
						value={questionInput}
						onChange={(e) => setQuestionInput(e.target.value)}
						InputProps={{
							style: {
								fontSize: 13,
								padding: "8px 14px",
							},
						}}
						InputLabelProps={{
							style: {
								fontSize: 13,
							},
						}}
						sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 } }}
					/>
				</FormControl>
				<FormControl fullWidth margin="dense">
					<Button variant="outlined" type="submit" disabled={addingQuestionStatus.status === RequestStatus.LOADING} sx={{ fontSize: 11 }}>
						{addingQuestionStatus.status === RequestStatus.LOADING ? (
							<>
								{translate("button_add")}
								<ProgressCircle isBtnDisabled />
							</>
						) : (
							translate("button_add")
						)}
					</Button>
				</FormControl>
			</form>
		</ModalFormWindow>
	);
};

export default FormAddingQuestion;
